<template>
  <div class="gg-preview-link">
    <div class="gg-preview-link__text">
      <span class="gg-preview-link__label">Link de visualização</span>
      <!-- While the link is being fetched or replaced, the URL line becomes a
           skeleton bar rather than showing a value that is about to change. -->
      <span
        v-if="updating"
        class="gg-preview-link__skeleton"
        aria-label="Atualizando link"
      />
      <a
        v-else-if="previewUrl"
        class="gg-preview-link__url"
        :href="`https://${previewUrl}`"
        :title="previewUrl"
        target="_blank"
        rel="noopener noreferrer"
        >{{ previewUrl }}</a
      >
      <span v-else class="gg-preview-link__url gg-preview-link__url--empty"
        >Nenhum link gerado</span
      >
    </div>

    <div class="gg-preview-link__actions">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <button
            type="button"
            class="gg-preview-link__btn"
            :disabled="!previewUrl || busy"
            aria-label="Copiar link de visualização"
            v-bind="attrs"
            v-on="on"
            @click="copyLink"
          >
            <v-icon size="20">mdi-content-copy</v-icon>
          </button>
        </template>
        <span>Copiar Link</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <button
            type="button"
            class="gg-preview-link__btn"
            :disabled="busy"
            :aria-label="
              previewUrl
                ? 'Gerar um novo link de visualização'
                : 'Gerar link de visualização'
            "
            v-bind="attrs"
            v-on="on"
            @click="regenerate"
          >
            <v-icon size="20">mdi-refresh</v-icon>
          </button>
        </template>
        <span>Atualizar link</span>
      </v-tooltip>
    </div>

    <transition name="gg-toast-fade">
      <GloboGraphicsToast v-if="toast" :variant="toast.variant" :text="toast.text" />
    </transition>
  </div>
</template>

<script>
import { apiClient } from "@/services/api";
import GloboGraphicsToast from "./GloboGraphicsToast.vue";

// How long a confirmation toast stays up.
const TOAST_MS = 3000;
// Broadcast on $root because the two components that can rotate this token are in
// different subtrees (toolbar vs the config modal in the header), so there is no
// prop/emit path between them. Exported so the modal binds the same string.
export const TOKEN_ROTATED_EVENT = "gg:service-token-rotated";

export default {
  name: "GloboGraphicsPreviewLink",
  components: { GloboGraphicsToast },
  props: {
    codProducao: { type: Number, required: true },
  },
  data() {
    return {
      token: null,
      loadingToken: false,
      busy: false,
      toast: null,
      toastTimer: null,
      // Monotonic request id — see fetchToken.
      tokenSeq: 0,
    };
  },
  computed: {
    domain() {
      return process.env.VUE_APP_ENV === "hml"
        ? "socialcam.h.apps.g.globo"
        : "socialcam.apps.g.globo";
    },
    previewUrl() {
      if (!this.token) return null;
      return `${this.domain}/cena?cod_producao=${this.codProducao}&token=${this.token}`;
    },
    updating() {
      return this.loadingToken || this.busy;
    },
  },
  created() {
    this.fetchToken();
    this.$root.$on(TOKEN_ROTATED_EVENT, this.onTokenRotated);
  },
  methods: {
    // Sequence-guarded: this component is NOT remounted when the production changes
    // (the toolbar has no :key on it), so switching A -> B leaves A's request in
    // flight. Without the guard, A's late response overwrites `token` while
    // `previewUrl` is already interpolating B's cod_producao — producing a link that
    // pairs one production with another's token and silently authenticates nothing.
    // The same applies to `loadingToken`, which the slower request would otherwise
    // clear and reveal the wrong URL.
    async fetchToken() {
      const seq = ++this.tokenSeq;
      this.loadingToken = true;
      try {
        const { data } = await apiClient.get(`tokenServico/${this.codProducao}`);
        if (seq !== this.tokenSeq) return;
        this.token = data.token ?? null;
      } catch {
        if (seq !== this.tokenSeq) return;
        this.token = null;
      } finally {
        if (seq === this.tokenSeq) this.loadingToken = false;
      }
    },
    // The same service token is rotatable from two places on this page: here, and
    // ModalControleTokenServico inside the production-config modal that the GG header
    // hosts. Each held the token in its own component state, so rotating in one left
    // the other showing — and copying — a revoked link for the rest of the session,
    // with no navigation to trigger a refetch. The payload carries the new token so
    // the listener updates directly instead of firing another GET.
    announceRotation(token) {
      this.$root.$emit(TOKEN_ROTATED_EVENT, { codProducao: this.codProducao, token });
    },
    onTokenRotated({ codProducao, token }) {
      if (String(codProducao) !== String(this.codProducao)) return;
      // Invalidate any fetch still in flight so it cannot land on top of this.
      this.tokenSeq++;
      this.loadingToken = false;
      this.token = token ?? null;
    },
    async copyLink() {
      if (!this.previewUrl) return;
      try {
        await navigator.clipboard.writeText(`https://${this.previewUrl}`);
        this.showToast("success", "Link de visualização copiado.");
      } catch {
        this.$toast?.error?.("Erro ao copiar link");
      }
    },
    showToast(variant, text) {
      clearTimeout(this.toastTimer);
      this.toast = { variant, text };
      this.toastTimer = setTimeout(() => {
        this.toast = null;
      }, TOAST_MS);
    },
    // Rotation is a single server-side row update, so the production is never
    // left without a token. The previous link stops working immediately: a
    // /cena page already open — including one on air — stays dark until it is
    // reloaded. Intentionally immediate, with no confirmation step.
    async regenerate() {
      this.busy = true;
      try {
        const { data } = await apiClient.post(`tokenServico/${this.codProducao}/rotate`);
        this.token = data.token;
        this.announceRotation(data.token);
        this.showToast("success", "Link de visualização atualizado.");
      } catch {
        this.$toast?.error?.("Erro ao gerar link.");
      } finally {
        this.busy = false;
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.toastTimer);
    this.$root.$off(TOKEN_ROTATED_EVENT, this.onTokenRotated);
  },
  watch: {
    codProducao() {
      this.token = null;
      this.fetchToken();
    },
  },
};
</script>

<style lang="scss" scoped>
.gg-preview-link {
  display: flex;
  align-items: center;
  gap: var(--sc-space-4);
}

.gg-preview-link__text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 44px;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.07px;
  white-space: nowrap;
}

.gg-preview-link__label {
  color: var(--sc-gg-text-title);
}

.gg-preview-link__url {
  overflow: hidden;
  width: 160px;
  color: var(--sc-gg-accent);
  text-decoration: none;
  text-overflow: ellipsis;

  &:hover {
    text-decoration: underline;
  }
}

.gg-preview-link__url--empty {
  color: var(--sc-gg-text-tertiary);
}

// Loading placeholder for the URL line — a bar fading out to the right.
.gg-preview-link__skeleton {
  width: 160px;
  height: 20px;
  border-radius: 2px;
  background: linear-gradient(
    to right,
    var(--sc-gg-disabled),
    rgba(var(--sc-gg-text-tertiary-rgb), 0)
  );
}

.gg-preview-link__actions {
  display: flex;
  align-items: center;
  gap: var(--sc-space-2);
}

.gg-preview-link__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 6px;
  background: transparent;
  border-radius: var(--sc-radius-lg);
  cursor: pointer;
  transition: background var(--sc-duration-base) var(--sc-easing-standard);

  .v-icon {
    color: var(--sc-gg-accent) !important;
  }

  // Hover = overlay/negative/1 state layer, per the DS control.
  &:hover:not(:disabled) {
    background: var(--sc-gg-overlay-1);
  }

  &:disabled {
    border-color: var(--sc-gg-disabled);
    cursor: not-allowed;

    .v-icon {
      color: var(--sc-gg-disabled) !important;
    }
  }
}
</style>
